import React from "react";
import "./Form.scss";
import { useAnalytics } from "../../customHooks/reactGA";
import { useNavigate } from "react-router-dom";
import formatPhoneNumber from "../../utils/formatPhoneNumber";
import emailjs from "@emailjs/browser";
import {
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";

const Form = (props) => {
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();

  const [customerName, setCustomerName] = React.useState("");
  const [address, setAddress] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [snackbar, setSnackbar] = React.useState({
    open: false,
    severity: "success",
    message: "",
  });

  const closeSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const clearForm = () => {
    setCustomerName("");
    setAddress("");
    setPhone("");
  };

  const splitName = (fullName) => {
    const parts = fullName.trim().split(/\s+/);
    return {
      first_name: parts[0] || "",
      last_name: parts.slice(1).join(" ") || "",
    };
  };

  const getTrackingParams = () => ({
    utm_source: sessionStorage.getItem("utm_source") || "",
    utm_medium: sessionStorage.getItem("utm_medium") || "",
    utm_campaign: sessionStorage.getItem("utm_campaign") || "",
    gclid: sessionStorage.getItem("gclid") || "",
    landing_page:
      sessionStorage.getItem("landing_page") || window.location.href,
    referrer: sessionStorage.getItem("referrer") || document.referrer || "",
  });

  const postToGHL = async () => {
    const { first_name, last_name } = splitName(customerName);
    const trackingParams = getTrackingParams();
    const phoneDigits = phone.replace(/\D/g, "");

    const payload = {
      first_name,
      last_name,
      phone: phoneDigits,
      property_address: address,
      ...trackingParams,
    };

    const response = await fetch(process.env.REACT_APP_GHL_WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`GHL webhook failed with status ${response.status}`);
    }
  };

  const sendEmail = async (e) => {
    const phoneDigits = phone.replace(/\D/g, "");

    e.preventDefault();
    if (isSubmitting) return;

    if (!customerName || !address || phoneDigits.length !== 10) {
      setSnackbar({
        open: true,
        severity: "warning",
        message:
          "Please fill out Name, Property Address, and a valid 10-digit Phone Number.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Keep existing notifications intact.
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          customer_name: customerName,
          property_address: address,
          phone: phoneDigits,
          source_page: window.location.pathname,
          submitted_at: new Date().toISOString(),
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      );

      // Send lead to GHL as an additional step.
      try {
        await postToGHL();
      } catch (ghlErr) {
        console.error("GHL webhook error:", ghlErr);
      }

      trackEvent("Lead Form", "Submit", "Step 1 Lead");
      window.gtag("event", "Thank_You");
      window.fbq?.("track", "Lead", {
        content_name: "Cash Offer Request",
      });

      navigate("/thank-you", {
        replace: true,
        state: { customerName, address, phone },
      });

      clearForm();
    } catch (err) {
      console.log(err);

      setSnackbar({
        open: true,
        severity: "error",
        message:
          "Sorry — something went wrong sending your request. Please try again or call us.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const labelColor =
    props.theme.palette.mode === "dark"
      ? props.theme.palette.common.white
      : props.theme.palette.primary.main;

  return (
    <div>
      <Box component="form" noValidate autoComplete="off" onSubmit={sendEmail}>
        <Box display="flex" flexDirection="column" alignContent="flex-start">
          <Box className="form-row" display="flex">
            <Box padding={1} flex={1}>
              <label>
                <Typography mb="0.25em" color={labelColor} fontSize="1rem">
                  Name
                </Typography>
              </label>
              <TextField
                fullWidth
                id="customer_name"
                name="customer_name"
                label="First and last name"
                variant="outlined"
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
              />
            </Box>
          </Box>

          <Box className="form-row" display="flex">
            <Box padding={1} flex={1}>
              <label>
                <Typography mb="0.25em" color={labelColor} fontSize="1rem">
                  Property Address
                </Typography>
              </label>
              <TextField
                fullWidth
                id="property_address"
                name="property_address"
                label="e.g., 123 Main St, Springfield, IN 46140"
                variant="outlined"
                value={address}
                onChange={(event) => setAddress(event.target.value)}
              />
            </Box>
          </Box>

          <Box className="form-row" display="flex">
            <Box padding={1} flex={1}>
              <label>
                <Typography mb="0.25em" color={labelColor} fontSize="1rem">
                  Phone Number
                </Typography>
              </label>
              <TextField
                fullWidth
                id="phone"
                name="phone"
                type="tel"
                inputProps={{
                  inputMode: "tel",
                  maxLength: 14,
                }}
                label="(000) 000-0000"
                variant="outlined"
                value={phone}
                onChange={(event) =>
                  setPhone(formatPhoneNumber(event.target.value))
                }
              />
            </Box>
          </Box>
        </Box>

        <br />

        <Box className="sbm-container" ml={1}>
          <Button
            type="submit"
            variant="contained"
            color="warning"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending..." : "Get My Cash Offer"}
          </Button>
          <Typography
            variant="body2"
            color={props.theme.palette.mode === "dark" ? "#cfcfcf" : "#6b6b6b"}
            sx={{ mt: 1, fontSize: "0.875rem" }}
          >
            We'll call you within 24 hours to discuss your property. No
            obligation to accept our offer.
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Form;
